// Composables
import { createRouter, createWebHistory } from 'vue-router';
import nhost from '@/plugins/nhost';
import SignIn from '@/views/auth/SignIn.vue';
import ForgotPassword from '@/views/auth/ForgotPassword.vue';
import SignUp from '@/views/auth/SignUp.vue';
import Logout from '@/views/auth/Logout.vue';
import ChangePassword from '@/views/auth/ChangePassword.vue';
import FirstAccess from '@/views/auth/FirstAccess.vue';
import { gql } from '@apollo/client/core';
import Dataroom from '@/views/analytics/Dataroom.vue';
import AcceptInvestorInvite from '@/views/InvestorInvite/AcceptInvestorInvite.vue';

const routes = [
  {
    path: '/',
    name: 'root',
    component: () => import(/* webpackChunkName: "public" */ '@/layouts/public/Default.vue'),
    children: [
      {
        path: '',
        name: 'signin-redirect',
        redirect: {
          name: 'signin',
        },
      },
      {
        path: 'sign-in',
        name: 'signin',
        component: SignIn,
      },
      {
        path: 'sign-up',
        name: 'signup',
        component: SignUp,
      },
      {
        path: 'forgot-password',
        name: 'forgotpassword',
        component: ForgotPassword,
      },
      {
        path: 'change-password',
        name: 'changepassword',
        component: ChangePassword,
      },
      {
        path: 'first-access',
        name: 'firstaccess',
        component: FirstAccess,
      },
      {
        path: 'investor-invite/:token',
        name: 'investorInviteAccept',
        component: AcceptInvestorInvite,
      },
    ],
  },
  {
    path: '/app',
    name: 'app',
    component: () => import(/* webpackChunkName: "private" */ '@/layouts/private/Default.vue'),
    meta: {
      auth: true,
    },
    children: [
      {
        path: '',
        name: 'appHome',
        redirect: {
          name: 'dashboard',
        },
      },
      {
        path: 'profile',
        name: 'profile',
        component: () => import(/* webpackChunkName: "profile" */ '@/views/user/Profile.vue'),
      },
      {
        path: 'my-companies',
        name: 'myCompanies',
        children: [
          {
            path: '',
            name: 'myCompaniesHome',
            component: () => import(/* webpackChunkName: "mycompanies" */ '@/views/company/MyCompanies.vue'),
          },
          {
            path: 'cockpit/:id',
            name: 'cockpit',
            meta: { breadcrumb: [{ titleKey: '$vuetify.my-companies.home.title.text', to: { name: 'myCompaniesHome' } }, { title: 'Cockpit' }] },
            component: () => import(/* webpackChunkName: "mycompanies-cockpit" */ '@/views/company/Cockpit/Home.vue'),
          },
          {
            path: 'cockpit/:id/financial',
            name: 'cockpitFinancial',
            meta: { breadcrumb: [{ titleKey: '$vuetify.my-companies.home.title.text', to: { name: 'myCompaniesHome' } }, { titleKey: '$vuetify.cockpit.financial.title.text' }] },
            component: () => import(/* webpackChunkName: "mycompanies-cockpit" */ '@/views/cockpit/Financial/Home.vue'),
          },
          {
            path: 'cockpit/:id/accounting',
            name: 'cockpitAccounting',
            meta: { breadcrumb: [{ titleKey: '$vuetify.my-companies.home.title.text', to: { name: 'myCompaniesHome' } }, { titleKey: '$vuetify.cockpit.accounting.title.text' }] },
            component: () =>
              import(/* webpackChunkName: "mycompanies-cockpit" */ '@/views/cockpit/Accounting/Home.vue'),
          },
          {
            path: 'cockpit/:id/indicators',
            name: 'cockpitIndicators',
            meta: { breadcrumb: [{ titleKey: '$vuetify.my-companies.home.title.text', to: { name: 'myCompaniesHome' } }, { titleKey: '$vuetify.cockpit.indicators.title.text' }] },
            component: () =>
              import(/* webpackChunkName: "mycompanies-cockpit" */ '@/views/cockpit/Indicators/Home.vue'),
          },
        ],
      },
      {
        path: 'diagnostico',
        name: 'diagnostico',
        meta: { breadcrumb: [{ titleKey: '$vuetify.diagnostico.title' }] },
        component: () => import(/* webpackChunkName: "diagnostico" */ '@/views/diagnostico/Home.vue'),
      },
      {
        path: 'cockpit',
        name: 'cockpitMenu',
        children: [
          {
            path: '',
            name: 'cockpitHome',
            meta: { breadcrumb: [{ title: 'Cockpit' }] },
            component: () => import(/* webpackChunkName: "cockpit" */ '@/views/cockpit/Home.vue'),
          },
          {
            path: 'financial',
            name: 'cockpitMenuFinancial',
            meta: { breadcrumb: [{ title: 'Cockpit', to: { name: 'cockpitHome' } }, { titleKey: '$vuetify.cockpit.financial.title.text' }] },
            component: () => import(/* webpackChunkName: "cockpit-financial" */ '@/views/cockpit/Financial/Home.vue'),
          },
          {
            path: 'accounting',
            name: 'cockpitMenuAccounting',
            meta: { breadcrumb: [{ title: 'Cockpit', to: { name: 'cockpitHome' } }, { titleKey: '$vuetify.cockpit.accounting.title.text' }] },
            component: () => import(/* webpackChunkName: "cockpit-accounting" */ '@/views/cockpit/Accounting/Home.vue'),
          },
          {
            path: 'indicators',
            name: 'cockpitMenuIndicators',
            meta: { breadcrumb: [{ title: 'Cockpit', to: { name: 'cockpitHome' } }, { titleKey: '$vuetify.cockpit.indicators.title.text' }] },
            component: () => import(/* webpackChunkName: "cockpit-indicators" */ '@/views/cockpit/Indicators/Home.vue'),
          },
          {
            path: 'reports',
            name: 'cockpitReports',
            children: [
              {
                path: '',
                name: 'cockpitReportsHome',
                meta: { breadcrumb: [{ title: 'Cockpit', to: { name: 'cockpitHome' } }, { titleKey: '$vuetify.cockpit.reports.home.title' }] },
                component: () =>
                  import(/* webpackChunkName: "cockpit-reports-home" */ '@/views/cockpit/Reports/Home.vue'),
              },
              {
                path: 'create',
                name: 'cockpitReportsCreate',
                meta: { breadcrumb: [{ title: 'Cockpit', to: { name: 'cockpitHome' } }, { titleKey: '$vuetify.cockpit.reports.home.title', to: { name: 'cockpitReportsHome' } }, { titleKey: '$vuetify.cockpit.reports.reports-form.title.text' }] },
                component: () =>
                  import(/* webpackChunkName: "cockpit-reports-create" */ '@/views/cockpit/Reports/Create.vue'),
              },
              {
                path: 'draft/:id/edit',
                name: 'cockpitReportsDraftEdit',
                meta: { breadcrumb: [{ title: 'Cockpit', to: { name: 'cockpitHome' } }, { titleKey: '$vuetify.cockpit.reports.home.title', to: { name: 'cockpitReportsHome' } }, { titleKey: '$vuetify.cockpit.reports.reports-form.title.text' }] },
                component: () =>
                  import(/* webpackChunkName: "cockpit-reports-create" */ '@/views/cockpit/Reports/Create.vue'),
              },
              {
                path: 'templates/:templateId/fill',
                name: 'cockpitReportsTemplateFill',
                meta: { breadcrumb: [{ title: 'Cockpit', to: { name: 'cockpitHome' } }, { titleKey: '$vuetify.cockpit.reports.home.title', to: { name: 'cockpitReportsHome' } }, { titleKey: '$vuetify.reports.templates.fill.title' }] },
                component: () =>
                  import(/* webpackChunkName: "cockpit-reports-template-fill" */ '@/views/cockpit/Reports/Templates/Fill.vue'),
              },
              {
                path: 'submission/:id/edit',
                name: 'cockpitReportsSubmissionEdit',
                meta: { breadcrumb: [{ title: 'Cockpit', to: { name: 'cockpitHome' } }, { titleKey: '$vuetify.cockpit.reports.home.title', to: { name: 'cockpitReportsHome' } }, { titleKey: '$vuetify.reports.templates.fill.title' }] },
                component: () =>
                  import(/* webpackChunkName: "cockpit-reports-template-fill" */ '@/views/cockpit/Reports/Templates/Fill.vue'),
              },
            ],
          },
          {
            path: 'sales',
            name: 'cockpitMenuSales',
            meta: { breadcrumb: [{ title: 'Cockpit', to: { name: 'cockpitHome' } }, { titleKey: '$vuetify.cockpit.sales.title.text' }] },
            component: () => import(/* webpackChunkName: "cockpit-sales" */ '@/views/cockpit/Sales/Home.vue'),
          },
          {
            path: 'operations',
            name: 'cockpitMenuOperations',
            meta: { breadcrumb: [{ title: 'Cockpit', to: { name: 'cockpitHome' } }, { titleKey: '$vuetify.cockpit.operations.title.text' }] },
            component: () => import(/* webpackChunkName: "cockpit-operations" */ '@/views/cockpit/Operations/Home.vue'),
          },
          {
            path: 'funding',
            name: 'cockpitMenuFunding',
            meta: { breadcrumb: [{ title: 'Cockpit', to: { name: 'cockpitHome' } }, { titleKey: '$vuetify.cockpit.funding.title.text' }] },
            component: () => import(/* webpackChunkName: "cockpit-funding" */ '@/views/cockpit/Funding/Home.vue'),
          },
        ],
      },
      {
        path: 'configuration',
        name: 'configurationMenu',
        children: [
          {
            path: '',
            name: 'configurationHome',
            meta: { breadcrumb: [{ titleKey: '$vuetify.configuration.title' }] },
            component: () => import(/* webpackChunkName: "configuration" */ '@/views/configuration/Home.vue'),
          },
          {
            path: 'financial',
            name: 'configurationMenuFinancial',
            meta: { breadcrumb: [{ titleKey: '$vuetify.configuration.title', to: { name: 'configurationHome' } }, { titleKey: '$vuetify.configuration.tabs.modules.cards.financial.title' }] },
            component: () =>
              import(/* webpackChunkName: "configuration-financial" */ '@/views/configuration/Financial/Home.vue'),
          },
          {
            path: 'financial/transactions',
            name: 'configurationMenuFinancialTransactions',
            meta: { breadcrumb: [{ titleKey: '$vuetify.configuration.title', to: { name: 'configurationHome' } }, { titleKey: '$vuetify.configuration.tabs.modules.cards.financial.title', to: { name: 'configurationMenuFinancial' } }, { titleKey: '$vuetify.configuration.financial.transactions.title' }] },
            component: () =>
              import(/* webpackChunkName: "configuration-financial-transactions" */ '@/views/configuration/Financial/Transactions.vue'),
          },
          {
            path: 'financial/conta-azul/callback',
            name: 'contaAzulWebhook',
            meta: { breadcrumb: [{ titleKey: '$vuetify.configuration.title', to: { name: 'configurationHome' } }, { titleKey: '$vuetify.configuration.tabs.modules.cards.financial.title' }] },
            component: () =>
              import(/* webpackChunkName: "configuration-financial" */ '@/views/configuration/Financial/Home.vue'),
          },
          {
            path: 'accounting',
            name: 'configurationMenuAccounting',
            meta: { breadcrumb: [{ titleKey: '$vuetify.configuration.title', to: { name: 'configurationHome' } }, { titleKey: '$vuetify.configuration.accounting.title' }] },
            component: () =>
              import(/* webpackChunkName: "configuration-accounting" */ '@/views/configuration/Accounting/Home.vue'),
          },
          {
            path: 'dataroom',
            name: 'configurationMenuDataroom',
            meta: { breadcrumb: [{ titleKey: '$vuetify.layout.menu.configuration.title', to: { name: 'configurationHome' } }, { titleKey: '$vuetify.layout.menu.dataroom.title' }] },
            component: () =>
              import(/* webpackChunkName: "configuration-dataroom" */ '@/views/configuration/Dataroom/Home.vue'),
          },
          {
            path: 'indicators',
            name: 'configurationMenuIndicators',
            meta: { breadcrumb: [{ titleKey: '$vuetify.configuration.title', to: { name: 'configurationHome' } }, { titleKey: '$vuetify.configuration.tabs.modules.cards.indicators.title' }] },
            component: () =>
              import(/* webpackChunkName: "configuration-indicators" */ '@/views/configuration/Indicators/Home.vue'),
          },
          {
            path: 'company-area',
            name: 'configurationMenuCompanyArea',
            meta: { breadcrumb: [{ titleKey: '$vuetify.configuration.title', to: { name: 'configurationHome' } }, { titleKey: '$vuetify.configuration.tabs.company-area.title' }] },
            component: () =>
              import(/* webpackChunkName: "configuration-company-area" */ '@/views/configuration/CompanyArea/Home.vue'),
          },
          {
            path: 'webhooks',
            name: 'configurationMenuWebhooks',
            meta: { breadcrumb: [{ titleKey: '$vuetify.configuration.title', to: { name: 'configurationHome' } }, { titleKey: '$vuetify.configuration.tabs.webhooks.title' }] },
            component: () =>
              import(/* webpackChunkName: "configuration-webhooks" */ '@/views/configuration/Webhooks/Home.vue'),
          },
          {
            path: 'api-keys',
            name: 'configurationMenuApiKeys',
            meta: { breadcrumb: [{ titleKey: '$vuetify.configuration.title', to: { name: 'configurationHome' } }, { titleKey: '$vuetify.configuration.tabs.api-keys.title' }] },
            component: () =>
              import(/* webpackChunkName: "configuration-api-keys" */ '@/views/configuration/ApiKeys/Home.vue'),
          },
          {
            path: 'investors',
            name: 'configurationMenuInvestors',
            meta: { breadcrumb: [{ titleKey: '$vuetify.configuration.title', to: { name: 'configurationHome' } }, { titleKey: '$vuetify.configuration.tabs.investors.title' }] },
            component: () =>
              import(/* webpackChunkName: "configuration-investors" */ '@/views/configuration/Investors/Home.vue'),
          },
        ],
      },
      {
        path: 'company-area',
        name: 'companyArea',
        meta: { breadcrumb: [{ titleKey: '$vuetify.company-area.title' }] },
        component: () => import(/* webpackChunkName: "company-area" */ '@/views/companyArea/Home.vue'),
      },
      {
        path: 'dashboard',
        name: 'dashboard',
        component: () => import(/* webpackChunkName: "dashboard" */ '@/views/analytics/Dashboard.vue'),
      },
      {
        path: 'dataroom',
        name: 'dataroom',
        component: Dataroom,
      },
      {
        path: 'portfolio',
        name: 'portfolio',
        children: [
          {
            path: '',
            name: 'portfolioHome',
            meta: { breadcrumb: [{ titleKey: '$vuetify.portfolio.title' }] },
            component: () => import(/* webpackChunkName: "portfolio" */ '@/views/portfolio/Home.vue'),
          },
          {
            path: 'users',
            name: 'portfolioCompanyUsers',
            meta: { breadcrumb: [{ titleKey: '$vuetify.portfolio.title', to: { name: 'portfolioHome' } }, { titleKey: '$vuetify.portfolio.company-users.title' }] },
            component: () => import(/* webpackChunkName: "portfolio-company-users" */ '@/views/portfolio/CompanyUsers/Home.vue'),
          },
          {
            path: 'templates',
            name: 'portfolioTemplates',
            meta: { breadcrumb: [{ titleKey: '$vuetify.portfolio.title', to: { name: 'portfolioHome' } }, { titleKey: '$vuetify.reports.templates.list.title' }] },
            component: () => import(/* webpackChunkName: "portfolio-templates" */ '@/views/portfolio/Templates/Home.vue'),
          },
          {
            path: 'templates/create',
            name: 'portfolioTemplateCreate',
            meta: { breadcrumb: [{ titleKey: '$vuetify.portfolio.title', to: { name: 'portfolioHome' } }, { titleKey: '$vuetify.reports.templates.list.title', to: { name: 'portfolioTemplates' } }, { titleKey: '$vuetify.reports.templates.form.title-create' }] },
            component: () => import(/* webpackChunkName: "portfolio-template-create" */ '@/views/portfolio/Templates/Create.vue'),
          },
          {
            path: 'templates/:id/edit',
            name: 'portfolioTemplateEdit',
            meta: { breadcrumb: [{ titleKey: '$vuetify.portfolio.title', to: { name: 'portfolioHome' } }, { titleKey: '$vuetify.reports.templates.list.title', to: { name: 'portfolioTemplates' } }, { titleKey: '$vuetify.reports.templates.form.title-edit' }] },
            component: () => import(/* webpackChunkName: "portfolio-template-create" */ '@/views/portfolio/Templates/Create.vue'),
          },
          {
            path: 'reports',
            name: 'portfolioReportAnswers',
            meta: { breadcrumb: [{ titleKey: '$vuetify.portfolio.title', to: { name: 'portfolioHome' } }, { titleKey: '$vuetify.portfolio.answers.title' }] },
            component: () => import(/* webpackChunkName: "portfolio-report-answers" */ '@/views/portfolio/Reports/Answers.vue'),
          },
          {
            path: 'reports/:clientId',
            name: 'portfolioClientReports',
            meta: { breadcrumb: [{ titleKey: '$vuetify.portfolio.title', to: { name: 'portfolioHome' } }, { titleKey: '$vuetify.reports.templates.published.title' }] },
            component: () => import(/* webpackChunkName: "portfolio-client-reports" */ '@/views/portfolio/Reports/Home.vue'),
          },
          {
            path: 'reports/:clientId/:submissionId',
            name: 'portfolioClientReportDetail',
            meta: { breadcrumb: [{ titleKey: '$vuetify.portfolio.title', to: { name: 'portfolioHome' } }, { titleKey: '$vuetify.reports.templates.published.title' }] },
            component: () => import(/* webpackChunkName: "portfolio-client-report-detail" */ '@/views/portfolio/Reports/Detail.vue'),
          },
        ],
      },
      {
        path: 'create-client',
        name: 'createClient',
        component: () => import(/* webpackChunkName: "create-client" */ '@/views/CreateClient/CreateClient.vue'),
      },
      {
        path: 'logout',
        name: 'logout',
        component: Logout,
      },
      {
        path: 'access-denied',
        name: 'accessDenied',
        component: () => import(/* webpackChunkName: "access-denied" */ '@/views/AccessDenied.vue'),
      },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

const INSERT_USER_EVENT = gql`
  mutation create($event_name: String, $extra_fields: jsonb) {
    insert_user_analysis_user_events_one(object: { event_name: $event_name, extra_fields: $extra_fields }) {
      event_name
    }
  }
`;

router.beforeEach(async to => {
  const authenticated = await nhost.auth.isAuthenticatedAsync();
  if (!authenticated && to.meta.auth) {
    return '/sign-in';
  }

  // Skip permission check for access-denied and logout routes
  if (to.name === 'accessDenied' || to.name === 'logout') {
    return true;
  }

  try {
    nhost.graphql.request(INSERT_USER_EVENT, {
      event_name: to.fullPath,
      extra_fields: {
        host: 'plataforma-web',
      },
    });
  } catch {
    /* empty */
  }

  return true;
});

export const menuItems = [
  {
    name: 'dashboard',
    route: '/app/dashboard',
    icon: 'FundOutlined',
    iconActive: 'FundFilled',
    disabled: false,
  },
  // {
  //   name: "myCompanies",
  //   route: "/app/my-companies",
  //   icon: "Star",
  // disabled: false,
  // },
  {
    name: 'cockpit',
    route: '/app/cockpit',
    icon: 'DashboardOutlined',
    iconActive: 'DashboardFilled',
    disabled: false,
  },
  {
    name: 'diagnostico',
    route: '/app/diagnostico',
    // A stethoscope, per TECH-2638. This is the one `mdi-*` entry in the menu: the rest are Ant
    // Design component names, and that set has no stethoscope. `getIcon` in Sidebar.vue passes an
    // unrecognised name through as a string so Vuetify resolves it from MDI. There is no filled
    // variant, so the active state reuses the same glyph.
    icon: 'mdi-stethoscope',
    iconActive: 'mdi-stethoscope',
    disabled: false,
  },
  {
    name: 'dataroom',
    route: '/app/dataroom',
    icon: 'MenuUnfoldOutlined',
    iconActive: 'MenuUnfoldOutlined',
    disabled: false,
  },
  {
    name: 'portfolio',
    route: '/app/portfolio',
    icon: 'ProjectOutlined',
    iconActive: 'ProjectFilled',
    disabled: false,
  },
  {
    name: 'company-area',
    route: '/app/company-area',
    icon: 'ExportOutlined',
    iconActive: 'ExportFilled',
    disabled: false,
  },
  {
    name: 'configuration',
    route: '/app/configuration',
    icon: 'SettingOutlined',
    iconActive: 'SettingFilled',
    disabled: false,
  },
  // {
  //   name: "profile",
  //   route: "/app/profile",
  //   icon: "Idcard",
  // },
  {
    name: 'logout',
    route: '/app/logout',
    icon: 'LogoutOutlined',
    iconActive: 'LogoutFilled',
    disabled: false,
  },
];

export default router;
